<script setup lang="ts">
import { computed, ref } from 'vue';
import DfInputSearch from '/@elements/DfInputSearch/DfInputSearch.vue';
import { Resource, ResourceFilter } from '/@types/resources';

const props = defineProps<{
  items: Array<Resource>;
  loadingGuids: Set<string>;
  filters?: Array<ResourceFilter>;
}>();

const emit = defineEmits<{
  add: [guid: string];
}>();

const search = ref('');
const showInfoGuid = ref<string | null>(null);

const filteredItems = computed(() =>
  props.items
    .filter(({ name }) => name.toLowerCase().includes(search.value.toLowerCase()))
    .map((item) => ({ ...item, inMultipleFilters: !!props.filters && item.filters.length > 0 })),
);
</script>

<template>
  <div class="add-list card">
    <df-input-search v-model="search" />

    <ul class="add-list__items">
      <li
        class="add-list__item"
        v-for="item in filteredItems"
        :key="item.guid"
      >
        <div
          class="add-list__button"
          :class="{ 'add-list__button--loading': loadingGuids.has(item.guid) }"
        >
          <button style="display: contents; text-align: left" v-on:click="emit('add', item.guid)">
            <div
              class="add-list__button__icon loading-icon"
              v-if="loadingGuids.has(item.guid)"
            ></div>
            <df-icon class="add-list__button__icon" code="f177" v-else />

            <div class="add-list__button__text">
              {{ item.name }}
              <small style="display: block" v-if="item.inMultipleFilters"
                >Er i {{ item.filters.length }} andre grupper</small
              >
            </div>
          </button>

          <button
            class="add-list__info-button fa-icon add-list__button__other"
            v-on:click="showInfoGuid = item.guid"
            v-if="item.inMultipleFilters"
          >
            &#xf05a;
          </button>
        </div>

        <df-modal v-if="filters && showInfoGuid === item.guid">
          <div class="dialog">
            <header class="head">
              <df-button v-on:click="showInfoGuid = null">
                <template v-slot:icon>
                  <df-icon code="f060" />
                </template>
              </df-button>

              <h1 class="title-md">{{ item.name }}</h1>
            </header>

            <dl class="dl card" style="padding: 0">
              <div class="dl__group dl__group--large">
                <dt class="dl__topic">Grupper</dt>
                <dd class="dl__description" v-for="filter in item.filters" :key="filter.id">
                  {{ filters.find(({ id }) => id === filter.id)?.name }}
                </dd>
              </div>
            </dl>
          </div>
        </df-modal>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.add-list {
  display: flex;
  flex-direction: column;
  gap: var(--gap-md);
}

.add-list__items {
  display: flex;
  flex-direction: column;
  gap: var(--gap-md);
}

.add-list__item {
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  align-items: center;
  gap: var(--gap-md);
}

@keyframes other-in {
  from {
    transform: translateX(calc(var(--shift) * -1));
  }
}

.add-list__button {
  --shift: 0;
  text-align: left;
  display: grid;
  grid-template-columns: max-content 1fr;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  align-items: center;
  gap: var(--gap-md);
  padding: var(--gap-md);
  background-color: var(--color-cardbg);
  border: none;
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
  width: calc(100% + var(--shift));
  transform: translateX(calc(var(--shift) * -1));
  transition: transform 0.15s;

  &:is(:hover, .add-list__button--loading) {
    --shift: var(--gap-lg);
    background-color: var(--color-hover);

    & .add-list__button__icon {
      opacity: 1;
    }

    & .add-list__button__text {
      transform: translateX(var(--shift));
    }

    & .add-list__button__other {
      animation: other-in 0.15s;
    }
  }
}

.add-list__button__icon {
  opacity: 0;
  transition: opacity 0.15s;
}

.add-list__button__text {
  transition: transform 0.15s;
}

.add-list__info-button {
  border-radius: var(--radius-sm);
  background-color: var(--color-cardbg);
  width: 1.5em;
  height: 1.5em;
  display: inline-grid;
  place-items: center;
  border: 0 solid transparent;
  box-shadow: var(--shadow-md);

  &:hover {
    background-color: var(--color-hover);
  }
}
</style>
